import { useState, useEffect } from 'react';

import { ApolloError } from '@apollo/client';
import { Button } from '@netfront/ui-library';
import { useRouter } from 'next/router';

import { ReportTable } from '../ReportTable';

import { OVERALL_TEXT_SPEECH_TABLE_COLUMNS } from './OverallTranslateTableView.constants';
import { getOverallProjectUsageTableData, groupAndSumProjectUsage } from './OverallTranslateTableView.helpers';
import { IGroupedProjectUsage, IOverallProjectUsageTableData, OverallTranslateTableViewProps } from './OverallTranslateTableView.interfaces';

import { useExportUsageReport, useGetProjectUsage, useToast } from '../../../../../hooks';
import { REPORTING_PAGE_CONSTANTS } from '../../ReportingPage.constants';

const { pageSize: defaultPageSize, tableSelectionItems } = REPORTING_PAGE_CONSTANTS;

const OverallTranslateTableView = ({ selectedTable, onTableChange, selectedView,  onViewChange, dateRange, onDateRangeChange }: OverallTranslateTableViewProps) => {
  const { query } = useRouter();
  const { handleToastError } = useToast();

  const { projectId } = query;

  const [filter, setFilter] = useState<string>('');
  const [allProjectUsages, setAllProjectUsages] = useState<IGroupedProjectUsage[]>([]);
  const [projectUsages, setProjectUsages] = useState<IGroupedProjectUsage[]>([]);
  const [projectUsageTableData, setProjectUsageTableData] = useState<IOverallProjectUsageTableData[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [isLoadMoreDisabled, setIsLoadMoreDisabled] = useState<boolean>(true);
  const [pageSize, setPageSize] = useState<number>(defaultPageSize);
  const [lastItemIndex, setLastItemIndex] = useState<number>(0);

  const handleApolloError = (error?: ApolloError) => {
    if (!error) {
      return;
    }

    handleToastError({
      error,
      shouldUseFriendlyErrorMessage: true,
    });
  };

  const { handleExportUsageReport, isLoading: isExportUsageReportLoading = false } = useExportUsageReport({
    fetchPolicy: 'no-cache',
    onCompleted: ({ report: { presignedUrl } }) => {
      window.open(presignedUrl, '_blank');
    },
    onError: (error) => {
      handleApolloError(error);
    },
  });

  const { handleGetProjectUsage, isLoading: isGetProjectUsageLoading = false } = useGetProjectUsage({
    fetchPolicy: 'no-cache',
    onCompleted: ({ edges }) => {

      const formattedProjectUsages = groupAndSumProjectUsage(edges.map((edge) => edge.node));

      setAllProjectUsages(formattedProjectUsages);
      setTotalItems(formattedProjectUsages.length);
      const paginatedItems = formattedProjectUsages.slice(0, pageSize);
      setIsLoadMoreDisabled(paginatedItems.length >= formattedProjectUsages.length || formattedProjectUsages.length <= pageSize);
      setProjectUsages(paginatedItems);
      setLastItemIndex(pageSize);

    },
    onError: (error) => {
      handleApolloError(error);
    },
  });

  const handleChangeSearchValue = (value?: string) => {
    setFilter(value ?? '');
  };

  const handleDownloadReport = () => {
    handleExportUsageReport({
      from: dateRange[0],
      ianaTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      projectId: String(projectId),
      to: dateRange[1],
    });
  };

  const handleGenerateTranslateReport = () => {
    handleGetProjectUsage({
      filter,
      from: dateRange[0],
      ianaTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      projectId: String(projectId ?? ''),
      to: dateRange[1],
    });
  };

  const handlePaginate = () => {
    return new Promise<void>((resolve) => {
      const updateIndex = lastItemIndex + pageSize;
      setLastItemIndex(updateIndex);
      setIsLoadMoreDisabled(totalItems <= updateIndex);
      setProjectUsages(allProjectUsages.slice(0, updateIndex));
      resolve();
    });
  };


  const handlePageSizeChange = (changedPageSize: number) => {
    setPageSize(changedPageSize);
    setLastItemIndex(changedPageSize);
    setIsLoadMoreDisabled(totalItems <= changedPageSize);
    setProjectUsages(allProjectUsages.slice(0, changedPageSize));
  };


  useEffect(() => {
    if (selectedTable !== tableSelectionItems[0].value) {
      return;
    }
    
    handleGenerateTranslateReport();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, selectedTable, filter]);

  useEffect(() => {
    setProjectUsageTableData(
      getOverallProjectUsageTableData({
        projectUsages,
      }),
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectUsages]);

  const isLoading = isGetProjectUsageLoading || isExportUsageReportLoading;

  return (
    <ReportTable<IOverallProjectUsageTableData> 
      childrenEnd={<Button size="xs" text="Download" type="button" onClick={() => handleDownloadReport()} />}
      columns={OVERALL_TEXT_SPEECH_TABLE_COLUMNS}
      data={projectUsageTableData}
      dateRange={dateRange}
      emptyStateMessage="There are no project usage data available yet"
      handleOnPageSizeChange={handlePageSizeChange}
      handleOnPaginate={handlePaginate}
      handleSearch={handleChangeSearchValue}
      isLoading={isLoading}
      isLoadMoreDisabled={isLoadMoreDisabled}
      pageSize={pageSize}
      selectedTable={selectedTable}
      selectedView={selectedView}
      totalItems={totalItems}
      onDateRangeChange={onDateRangeChange}
      onTableChange={onTableChange}
      onViewChange={onViewChange}
    />
  );
};

export { OverallTranslateTableView };
